import { useMemo, useState } from 'react'
import type { PartyDistrictStateWithDistrict } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface PartyStrengthTableProps {
  districtStates: PartyDistrictStateWithDistrict[]
  isLoading?: boolean
}

function StrengthBar({ value, label }: { value: number; label: string }) {
  // Color based on value: low (red), medium (yellow), high (green)
  const getColorClass = (val: number) => {
    if (val >= 60) return 'bg-green-500'
    if (val >= 30) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  return (
    <div className="space-y-1">
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full transition-all ${getColorClass(value)}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

export function PartyStrengthTable({
  districtStates,
  isLoading = false,
}: PartyStrengthTableProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [regionFilter, setRegionFilter] = useState<string>('all')

  // Extract unique regions for filter dropdown
  const regions = useMemo(() => {
    const regionSet = new Set<string>()
    districtStates.forEach((state) => {
      if (state.district?.region?.name) {
        regionSet.add(state.district.region.name)
      }
    })
    return Array.from(regionSet).sort()
  }, [districtStates])

  // Filter and sort district states
  const filteredStates = useMemo(() => {
    return districtStates
      .filter((state) => {
        // Filter by region
        if (regionFilter !== 'all' && state.district?.region?.name !== regionFilter) {
          return false
        }
        // Filter by search query
        if (searchQuery) {
          const query = searchQuery.toLowerCase()
          const districtName = state.district?.name?.toLowerCase() || ''
          const regionName = state.district?.region?.name?.toLowerCase() || ''
          return districtName.includes(query) || regionName.includes(query)
        }
        return true
      })
      .sort((a, b) => {
        // Sort by region name, then district name
        const regionCompare = (a.district?.region?.name || '').localeCompare(
          b.district?.region?.name || ''
        )
        if (regionCompare !== 0) return regionCompare
        return (a.district?.name || '').localeCompare(b.district?.name || '')
      })
  }, [districtStates, searchQuery, regionFilter])

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">District Strength</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 animate-pulse">
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
            <div className="h-48 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base">District Strength</CardTitle>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Filter by region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search districts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-[200px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredStates.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery || regionFilter !== 'all'
              ? 'No districts match your filters'
              : 'No district data available'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Region</TableHead>
                <TableHead>District</TableHead>
                <TableHead className="w-[140px]">Recognition</TableHead>
                <TableHead className="w-[140px]">Organization</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStates.map((state) => (
                <TableRow key={state.id}>
                  <TableCell className="font-medium">
                    {state.district?.region?.name || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div>{state.district?.name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground">
                        {state.district?.settlement_type} -{' '}
                        {state.district?.population?.toLocaleString() || 0} pop
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StrengthBar value={state.recognition} label={`${state.recognition}%`} />
                  </TableCell>
                  <TableCell>
                    <StrengthBar
                      value={state.organization_strength}
                      label={`${state.organization_strength}%`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="mt-4 text-xs text-muted-foreground text-right">
          Showing {filteredStates.length} of {districtStates.length} districts
        </div>
      </CardContent>
    </Card>
  )
}
